import { Linking, Platform } from 'react-native';
import * as Location from 'expo-location';
import { useMutation, type UseMutationResult } from '@tanstack/react-query';

export type Coords = { lat: number; lng: number };

// Mirrors use-tier0-state.ts's Tier0Phase/deriveTier0Phase split, for
// the same reason: permission-denied comes from a completely separate
// I/O source (requestForegroundPermissionsAsync) than the actual
// position fetch (getCurrentPositionAsync), so a plain
// isPending/isError/isSuccess read of one mutation can't express it on
// its own. Kept as a real discriminated union (not e.g. a boolean
// "loading" + nullable coords + nullable error) so a caller screen can
// render an exhaustive switch instead of juggling independent flags
// that could disagree with each other.
export type LocationPhase =
  | { phase: 'idle' }
  | { phase: 'denied' }
  | { phase: 'locating' }
  | { phase: 'granted'; coords: Coords }
  | { phase: 'error'; message: string };

// Thrown by the mutation below when the OS-level permission prompt is
// declined -- a typed sentinel so deriveLocationPhase can tell "denied"
// (its own UI: an Open Settings prompt) apart from every other failure
// (a generic error message), without a second, independent boolean that
// could disagree with the mutation's own status (see this file's
// history: an earlier version tracked permission denial in separate
// React state, set only on the *resolved* "not granted" case -- any
// exception thrown by requestForegroundPermissionsAsync or
// getCurrentPositionAsync itself, e.g. no geolocation API on an
// insecure origin, was an unhandled promise rejection with no UI
// feedback at all, since requestLocation() below is invoked
// fire-and-forget from onPress and awaited by nothing. Folding the
// permission request into the mutation itself means every failure --
// denied, or any real exception -- is now the SAME mutation's isError,
// with nothing left outside it to fall silently out of sync).
export class LocationPermissionDeniedError extends Error {}

// Pure, unit-tested separately from the hook below -- same rationale
// as deriveTier0Phase: no React tree or mocked native module needed to
// exercise the branching. Order matters here too: a mutation in flight
// or already resolved always wins over a stale permission-denied flag,
// since a successful getCurrentPositionAsync call necessarily means
// permission was granted for this attempt.
export function deriveLocationPhase(
  mutation: Pick<UseMutationResult<Coords, Error, void>, 'isPending' | 'isError' | 'error' | 'isSuccess' | 'data'>,
): LocationPhase {
  if (mutation.isPending) return { phase: 'locating' };

  if (mutation.isError) {
    if (mutation.error instanceof LocationPermissionDeniedError) {
      return { phase: 'denied' };
    }

    return {
      phase: 'error',
      message: mutation.error?.message || "Couldn't get your location. Check your connection and try again.",
    };
  }

  if (mutation.isSuccess && mutation.data) {
    return { phase: 'granted', coords: mutation.data };
  }

  return { phase: 'idle' };
}

// Originally profile-only (issue #14); issue #15's create-thread
// screen became a second call site reading the exact same mechanism
// (permission request -> getCurrentPositionAsync -> discriminated
// phase) with no profile-specific behavior in it, so it was
// generalized here rather than duplicated -- unlike useTier0State
// above, which stays its own hook because its I/O (camera capture) and
// failure modes are genuinely different, not just similarly shaped.
// The two call sites still differ in how they *use* the result: the
// profile screen persists it as a long-lived setting with an "Update"
// affordance, create-thread reads it once per draft with no re-request
// UI -- that presentation difference belongs in each screen, not here.
export function useCurrentLocation(): {
  phase: LocationPhase;
  requestLocation: () => void;
  openSettings: () => void;
  canOpenSettings: boolean;
} {
  const locate = useMutation({
    // Requested fresh on every call, not cached -- same reasoning as
    // useTier0State's capture(): a user who denies once, then grants it
    // from Settings, should have their very next tap succeed without an
    // extra "try again" step. Permission request and position fetch
    // share one mutationFn (see LocationPermissionDeniedError above) so
    // a thrown exception from either -- not just a resolved "not
    // granted" -- reaches the UI.
    mutationFn: async (): Promise<Coords> => {
      const permission = await Location.requestForegroundPermissionsAsync();
      if (!permission.granted) {
        throw new LocationPermissionDeniedError();
      }

      const position = await Location.getCurrentPositionAsync({});
      return { lat: position.coords.latitude, lng: position.coords.longitude };
    },
  });

  function requestLocation() {
    if (locate.isPending) return;
    locate.mutate();
  }

  function openSettings() {
    // Same web gap as useTier0State's openSettings -- react-native-web's
    // Linking has no openSettings implementation.
    void Linking.openSettings();
  }

  return {
    phase: deriveLocationPhase(locate),
    requestLocation,
    openSettings,
    canOpenSettings: Platform.OS !== 'web',
  };
}
